import { gql } from '@apollo/client';

/**
 * Reusable selection for AI clip notes (clipName + messages).
 * Use on types that have notes (e.g. AiClipRecord, mutation payloads).
 */
export const AI_CLIP_NOTES_FRAGMENT = gql`
  fragment AiClipNotesFields on AiClipRecord {
    notes {
      clipName
      messages {
        id
        authorId
        authorName
        createdAt
        text
      }
    }
  }
`;

/**
 * List interviews with their AI clip via the loader on Interview type.
 * Uses backend getInterviews (offset/limit pagination) with the clip field.
 */
export const GET_INTERVIEWS_WITH_CLIPS = gql`
  query GetInterviewsWithClips($input: GetInterviewsInput!) {
    getInterviews(input: $input) {
      entities {
        id
        createdAt
        status
        finalStreamTitle
        interviewee {
          id
          slug
          firstName
          lastName
          storeDetails {
            storeName
          }
        }
        interviewer {
          id
          slug
          firstName
          lastName
          storeDetails {
            storeName
          }
        }
        stream {
          slug
          store {
            id
            slug
            firstName
            lastName
            storeDetails {
              storeName
            }
          }
        }
        clip {
          userId
          interviewId
          jobId
          status
          profileName
          videoUrl
          inputVideoCreatedAt
          outputVideoCreatedAt
          outputBucket
          outputPrefix
          clipsGenerated
          errorMessage
          ...AiClipNotesFields
          uploadedFileList {
            name
            s3Key
            url
          }
        }
      }
      total
      offset
      limit
    }
  }
  ${AI_CLIP_NOTES_FRAGMENT}
`;

/**
 * Get single AI clip by user + stream slug.
 * Backend: getAiClip(input: GetAiClipInput!): AiClipRecord
 */
export const GET_AI_CLIP = gql`
  query GetAiClip($userSlug: String!, $streamSlug: String!) {
    getAiClip(input: { userSlug: $userSlug, streamSlug: $streamSlug }) {
      userId
      interviewId
      jobId
      status
      profileName
      videoUrl
      inputVideoCreatedAt
      outputVideoCreatedAt
      outputBucket
      outputPrefix
      clipsGenerated
      errorMessage
      ...AiClipNotesFields
      uploadedFileList {
        name
        s3Key
        url
        title
        topic
        score
      }
    }
  }
  ${AI_CLIP_NOTES_FRAGMENT}
`;

/**
 * A stream's own recordings, in air-time order. Read from Postgres rather than derived from
 * the clip records, which hold one video each: an interrupted PPV stream or an interview the
 * recorder split into a main part and a short tail has several recordings. `streamRecords`
 * returns the Landscape rendition of each (and records with no aspect ratio), already sorted by
 * air time — the same source the public Stream Playlist uses.
 */
export const GET_STREAM_RECORDINGS = gql`
  query GetStreamRecordings($slug: String!) {
    adminStreams(
      input: { slug: $slug, limit: 1, offset: 0, withCopies: true }
    ) {
      entities {
        id
        streamRecords {
          id
          videoURL
          videoThumbnailURL
          duration
        }
      }
    }
  }
`;

/**
 * Get presigned S3 URL for a clip file (1h expiry).
 * Backend: getAiClipPresignedUrl(input: GetAiClipPresignedUrlInput!): String!
 */
export const GET_AI_CLIP_PRESIGNED_URL = gql`
  query GetAiClipPresignedUrl($s3Uri: String!) {
    getAiClipPresignedUrl(input: { s3Uri: $s3Uri })
  }
`;

export const UPDATE_AI_CLIP_NOTES = gql`
  mutation UpdateAiClipNotes($input: UpdateAiClipNotesInput!) {
    updateAiClipNotes(input: $input) {
      userId
      interviewId
      ...AiClipNotesFields
    }
  }
  ${AI_CLIP_NOTES_FRAGMENT}
`;

/**
 * Minimal query to resolve a user slug or name to a user id.
 */
export const LOOKUP_STORE_USER = gql`
  query LookupStoreUser($input: AdminStoresFilterInput!) {
    adminStores(input: $input) {
      entities {
        id
      }
    }
  }
`;

export const ADD_AI_CLIP_NOTE = gql`
  mutation AddAiClipNote($input: AddAiClipNoteInput!) {
    addAiClipNote(input: $input) {
      userId
      interviewId
      ...AiClipNotesFields
    }
  }
  ${AI_CLIP_NOTES_FRAGMENT}
`;
